import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Brain,
  CalendarClock,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  computeEntityDeadlines,
  deleteEntityMemorySource,
  fetchEntityDeadlines,
  fetchEntityFacts,
  setEntityFact,
  type EntityDeadline,
  type EntityFact,
} from '../../lib/api';

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function daysUntil(iso: string): number {
  const due = new Date(`${iso.slice(0, 10)}T00:00:00`).getTime();
  return Math.ceil((due - Date.now()) / 86400000);
}

function deadlineTone(days: number): string {
  if (days < 0) return 'border-zt-border text-zt-muted';
  if (days <= 14) return 'border-zt-danger/40 text-zt-danger';
  if (days <= 45) return 'border-amber-500/40 text-amber-400';
  return 'border-zt-border text-slate-300';
}

export default function MemoryPanel() {
  const [facts, setFacts] = useState<EntityFact[]>([]);
  const [deadlines, setDeadlines] = useState<EntityDeadline[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ category: 'entity', factKey: '', value: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const [factRes, deadlineRes] = await Promise.all([
      fetchEntityFacts(),
      fetchEntityDeadlines(365),
    ]);
    setFacts(factRes.ok && factRes.data ? factRes.data.facts : []);
    setDeadlines(deadlineRes.ok && deadlineRes.data ? deadlineRes.data.deadlines : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = facts.filter(
      (f) =>
        !needle ||
        f.factKey.toLowerCase().includes(needle) ||
        f.category.toLowerCase().includes(needle) ||
        renderValue(f.value).toLowerCase().includes(needle)
    );
    const map = new Map<string, EntityFact[]>();
    for (const fact of filtered) {
      const list = map.get(fact.category) ?? [];
      list.push(fact);
      map.set(fact.category, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [facts, query]);

  const handleAdd = async () => {
    if (!draft.factKey.trim() || !draft.value.trim()) return;
    setBusy(true);
    const res = await setEntityFact({
      category: draft.category.trim() || 'entity',
      fact_key: draft.factKey.trim(),
      value: draft.value.trim(),
      confidence: 1,
    });
    setBusy(false);
    if (res.ok) {
      toast.success('Fact saved to memory');
      setDraft({ category: 'entity', factKey: '', value: '' });
      setAdding(false);
      void load();
    }
  };

  const handleForget = async (fact: EntityFact) => {
    if (!fact.sourceDocumentId) {
      toast.error('Manually added facts can only be overwritten, not deleted.');
      return;
    }
    setBusy(true);
    const res = await deleteEntityMemorySource(fact.sourceDocumentId);
    setBusy(false);
    if (res.ok) {
      toast.success('Forgot everything learned from that document');
      void load();
    }
  };

  const handleRecompute = async () => {
    setBusy(true);
    const res = await computeEntityDeadlines(true);
    setBusy(false);
    if (res.ok && res.data) {
      setDeadlines(res.data.deadlines);
      toast.success(`Recomputed ${res.data.count} deadlines`);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="space-y-2 border-b border-zt-border p-3">
        <div className="flex items-center gap-2">
          <Brain size={14} className="text-zt-primary" />
          <span className="text-sm font-medium text-white">Entity memory</span>
          <span className="text-xs text-zt-muted">{facts.length} facts</span>
          <button
            type="button"
            onClick={() => void load()}
            className="ml-auto rounded p-1 text-zt-muted hover:text-zt-primary"
            aria-label="Refresh memory"
            title="Refresh"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="rounded p-1 text-zt-muted hover:text-zt-primary"
            aria-label="Add fact"
            title="Add fact"
          >
            <Plus size={14} />
          </button>
        </div>
        <p className="text-[11px] leading-relaxed text-zt-muted">
          Facts here are injected into every assistant prompt. Sensitive identifiers stay in the
          secure vault and are never stored as memory.
        </p>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zt-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search facts"
            className="w-full rounded-lg border border-zt-border bg-zt-bg py-1.5 pl-8 pr-2 text-xs text-slate-200 placeholder-zt-muted focus:border-zt-primary focus:outline-none"
          />
        </div>

        {adding && (
          <div className="space-y-1.5 rounded-lg border border-zt-border bg-zt-bg p-2">
            <div className="flex gap-1.5">
              <input
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                placeholder="category"
                className="w-24 rounded border border-zt-border bg-zt-surface px-2 py-1 text-xs text-slate-200 focus:border-zt-primary focus:outline-none"
              />
              <input
                value={draft.factKey}
                onChange={(e) => setDraft({ ...draft, factKey: e.target.value })}
                placeholder="fact key"
                className="min-w-0 flex-1 rounded border border-zt-border bg-zt-surface px-2 py-1 text-xs text-slate-200 focus:border-zt-primary focus:outline-none"
              />
            </div>
            <input
              value={draft.value}
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
              placeholder="value"
              className="w-full rounded border border-zt-border bg-zt-surface px-2 py-1 text-xs text-slate-200 focus:border-zt-primary focus:outline-none"
            />
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={busy}
              className="w-full rounded bg-zt-primary px-2 py-1 text-xs font-medium text-zt-bg hover:bg-zt-accent disabled:opacity-50"
            >
              Save fact
            </button>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-zt-muted">
            <Loader2 size={14} className="animate-spin" /> Loading memory…
          </div>
        )}

        {!loading && facts.length === 0 && (
          <p className="text-sm text-zt-muted">
            Nothing learned yet. Attach your formation documents in chat and the assistant will
            extract entity facts automatically.
          </p>
        )}

        {grouped.map(([category, items]) => (
          <div key={category}>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-zt-muted">
              {category}
            </p>
            <ul className="space-y-1">
              {items.map((fact) => (
                <li
                  key={fact.id}
                  className="group flex items-start gap-2 rounded-lg border border-zt-border bg-zt-bg px-2.5 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] text-zt-muted">{fact.factKey}</p>
                    <p className="break-words text-xs text-slate-200">{renderValue(fact.value)}</p>
                  </div>
                  <span className="shrink-0 text-[10px] text-zt-muted">
                    {Math.round((fact.confidence ?? 0) * 100)}%
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleForget(fact)}
                    disabled={busy}
                    className="shrink-0 text-zt-muted opacity-0 transition hover:text-zt-danger group-hover:opacity-100"
                    aria-label="Forget source document"
                    title="Forget everything from this source document"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="pt-2">
          <div className="mb-1 flex items-center gap-2">
            <CalendarClock size={13} className="text-zt-primary" />
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zt-muted">
              Deadlines
            </p>
            <button
              type="button"
              onClick={() => void handleRecompute()}
              disabled={busy}
              className="ml-auto text-[10px] text-zt-muted hover:text-zt-primary disabled:opacity-50"
            >
              Recompute
            </button>
          </div>
          {deadlines.length === 0 ? (
            <p className="text-xs text-zt-muted">
              No deadlines computed. Add entity type and formation date to derive them.
            </p>
          ) : (
            <ul className="space-y-1">
              {deadlines.map((d) => {
                const days = daysUntil(d.dueDate);
                return (
                  <li
                    key={d.id}
                    className={`rounded-lg border bg-zt-bg px-2.5 py-2 text-xs ${deadlineTone(days)}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-slate-200">{d.title}</span>
                      <span className="shrink-0">
                        {days < 0 ? 'past due' : days === 0 ? 'today' : `${days}d`}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[10px] text-zt-muted">
                      {d.dueDate}
                      {d.formId ? ` · ${d.formId}` : ''}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
